import Seo from "@/components/seo/Seo";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { ZoneSelector } from "@/components/ZoneSelector";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useZone } from "@/contexts/ZoneContext";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import type { SearchCategory } from "@/lib/searchCategories";
import {
  TOPIC_CATEGORIES,
  getPopularTopics,
  getTopicCategoryLabel,
  getTopicDescription,
  getTopicLabel,
} from "@shared/topic-taxonomy";
import {
  getTopicProblem,
  getTopicProblemUrgencyLabel,
} from "@shared/topic-problems";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  ChevronDown,
  Languages,
  MapPin,
  MessageCircle,
  Newspaper,
  Search,
  ShoppingBag,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

const commonTopics = getPopularTopics();

const homeSearchCategories: SearchCategory[] = [
  { id: "news", label: "뉴스", subCategories: [] },
  { id: "community", label: "동네마켓", subCategories: [] },
  { id: "community-talk", label: "동네 톡톡", subCategories: [] },
  { id: "local-business", label: "업체찾기", subCategories: [] },
  { id: "topics", label: "생활문제 해결 가이드", subCategories: [] },
  { id: "meetup", label: "교민모임", subCategories: [] },
];

const searchCategoryLabelsEn: Record<string, string> = {
  topics: "Life problem help",
  news: "News",
  community: "Local Market",
  "used-goods": "Used goods",
  "used-car": "Cars",
  "real-estate": "Property",
  job: "Jobs",
  "community-talk": "Life questions & experiences",
  meetup: "Meetups & events",
  "local-business": "Services",
};

const serviceCategories = [
  { ko: "병원·약국", en: "Healthcare", category: "medical" },
  { ko: "회계·세금", en: "Accounting & tax", category: "accounting" },
  { ko: "이사·청소", en: "Moving & cleaning", category: "moving" },
  { ko: "교육·과외", en: "Education & tutoring", category: "education" },
  { ko: "수리·정비", en: "Repairs & maintenance", category: "repair" },
  { ko: "음식·카페", en: "Food & cafes", category: "restaurant" },
  { ko: "법률·이민", en: "Legal & migration", category: "legal" },
  { ko: "부동산 서비스", en: "Property services", category: "realestate" },
];

type HomeLocalItem = {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  meta: string;
};

type HomeLocalSections = {
  market: HomeLocalItem[];
  talk: HomeLocalItem[];
  meetings: HomeLocalItem[];
  businesses: HomeLocalItem[];
  news: HomeLocalItem[];
};

const emptyLocalSections: HomeLocalSections = {
  market: [],
  talk: [],
  meetings: [],
  businesses: [],
  news: [],
};

function getArrayPayload(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value as Record<string, unknown>[];
  if (!value || typeof value !== "object") return [];

  const record = value as Record<string, unknown>;
  if (Array.isArray(record.data)) return record.data as Record<string, unknown>[];
  if (Array.isArray(record.items)) return record.items as Record<string, unknown>[];

  if (record.data && typeof record.data === "object") {
    const nestedItems = (record.data as Record<string, unknown>).items;
    if (Array.isArray(nestedItems)) {
      return nestedItems as Record<string, unknown>[];
    }
  }

  return [];
}

function textValue(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function optionalTextValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function SectionHeader({
  eyebrow,
  title,
  description,
  href,
  actionLabel,
}: {
  eyebrow: string;
  title: string;
  description: string;
  href?: string;
  actionLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold text-primary">{eyebrow}</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          {title}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground md:text-base">
          {description}
        </p>
      </div>
      {href && actionLabel && (
        <Link href={href}>
          <Button variant="ghost" className="w-fit gap-2 px-0 text-primary">
            {actionLabel}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      )}
    </div>
  );
}

function ItemGrid({
  items,
  isLoading,
  emptyTitle,
  emptyDescription,
  emptyHref,
}: {
  items: HomeLocalItem[];
  isLoading: boolean;
  emptyTitle: string;
  emptyDescription: string;
  emptyHref: string;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="rounded-2xl border border-border bg-card p-5">
            <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
            <div className="mt-3 h-3 w-full animate-pulse rounded bg-muted" />
            <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Link href={emptyHref}>
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 transition-colors hover:bg-accent">
          <p className="font-semibold text-foreground">{emptyTitle}</p>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {emptyDescription}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {items.map((item) => (
        <Link key={item.id} href={item.href}>
          <article className="group h-full rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm">
            <h3 className="line-clamp-2 font-semibold leading-6 text-foreground group-hover:text-primary">
              {item.title}
            </h3>
            {item.subtitle && (
              <p className="mt-2 line-clamp-3 text-sm leading-6 text-muted-foreground">
                {item.subtitle}
              </p>
            )}
            <p className="mt-4 text-xs font-medium text-muted-foreground">
              {item.meta}
            </p>
          </article>
        </Link>
      ))}
    </div>
  );
}

function LocalPanel({
  icon: Icon,
  title,
  href,
  items,
  isLoading,
  emptyTitle,
}: {
  icon: LucideIcon;
  title: string;
  href: string;
  items: HomeLocalItem[];
  isLoading: boolean;
  emptyTitle: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <h3 className="font-semibold text-foreground">{title}</h3>
        </div>
        <Link href={href}>
          <Button variant="ghost" size="icon" aria-label={title}>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="mt-5 space-y-4">
          {[0, 1].map((item) => (
            <div key={item}>
              <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : items.length > 0 ? (
        <div className="mt-4 divide-y divide-border">
          {items.slice(0, 3).map((item) => (
            <Link key={item.id} href={item.href}>
              <div className="py-3 first:pt-0 last:pb-0">
                <p className="line-clamp-1 text-sm font-medium text-foreground hover:text-primary">
                  {item.title}
                </p>
                <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                  {item.meta}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <Link href={href}>
          <div className="mt-5 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground transition-colors hover:bg-accent">
            {emptyTitle}
          </div>
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const { locale, setLocale, isKorean } = useTopicLocale();
  const { selectedZoneId, selectedZone } = useZone();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<SearchCategory | null>(null);

  const copy = isKorean
    ? {
        seoTitle: "OZK — 뉴스·동네 마켓·동네 톡톡·업체찾기",
        seoDescription:
          "비자, 렌트, 세금, 학교, 병원과 동네 정보를 찾고 질문, 경험, 모임과 업체로 다음 행동을 이어가세요.",
        heroTitle: "호주 생활문제, 한국어로 빠르게 해결하세요",
        heroLine1:
          "비자, 렌트, 세금, 학교, 병원과 일자리 문제를 이해하고, 필요한 정보와 업체를 바로 찾아보세요.",
        heroLine2: "",
        searchPlaceholder: "어떤 문제가 있으신가요? 예: 본드를 돌려받지 못했어요",
        searchLabel: "OZK 통합 검색",
        helpPrompt: "원하는 답을 찾지 못하셨나요? 카톡으로 편하게 물어보세요.",
        findTopicsCta: "생활문제 찾아보기",
        all: "전체",
        commonTopics: "지금 많이 막히는 호주생활 문제",
        commonTopicsDescription:
          "제도 이름을 몰라도 괜찮습니다. 내 상황과 가장 가까운 문제부터 선택하세요.",
        viewAllTopics: "생활문제 전체보기",
        questions: "질문과 해결 경험",
        questionsDescription:
          "궁금한 것은 질문으로, 직접 해결한 과정은 경험으로, 가벼운 이야기는 동네 톡톡으로 나누세요.",
        moreQuestions: "동네 톡톡 더보기",
        emptyQuestions: "아직 표시할 동네 이야기가 없습니다.",
        emptyQuestionsDescription: "첫 번째 동네 이야기를 남겨보세요.",
        intentQuestion: "질문",
        intentExperience: "해결 경험",
        intentLegacy: "동네 이야기",
        meetups: "관심사로 만나는 교민모임",
        meetupsDescription:
          "온라인 질문이 아니라 실제로 참여할 수 있는 일정, 장소와 주최자가 있는 만남입니다.",
        moreMeetups: "교민모임 더보기",
        emptyMeetups: "현재 표시할 모임이 없습니다.",
        emptyMeetupsDescription:
          "다른 지역을 선택하거나 모임 목록을 확인해보세요.",
        myArea: "동네 생활",
        myAreaDescription: `${selectedZone.label} 기준 모임과 동네 마켓을 한곳에서 확인하세요.`,
        write: "글쓰기",
        localNews: "지역 소식",
        localServices: "동네 업체",
        localMarket: "동네마켓",
        noNews: "현재 표시할 지역 소식이 없습니다.",
        noServices: "현재 표시할 업체가 없습니다.",
        noMarket: "현재 표시할 거래글이 없습니다.",
        services: "가까운 한인업체",
        servicesDescription: "한국어로 상담할 수 있는 업체를 찾아보세요.",
        browseServices: "업체 전체보기",
        news: "뉴스와 업데이트",
        newsDescription:
          "호주 생활에 참고할 주요 뉴스와 지역 소식을 확인하세요.",
        moreNews: "뉴스 더보기",
        emptyNews: "현재 표시할 뉴스가 없습니다.",
        emptyNewsDescription: "뉴스 목록에서 최신 글을 확인해보세요.",
        market: "동네 마켓",
        marketDescription:
          "중고물품, 자동차, 부동산을 하나의 동네 마켓에서 사고팔고 찾아보세요.",
        marketEyebrow: "통합 동네마켓",
        browseMarket: "전체 마켓 보기",
        shortcuts: {
          topics: "뉴스",
          area: "동네 마켓",
          meetups: "동네 톡톡",
          services: "업체찾기",
        },
      }
    : {
        seoTitle: "OZK — Life in Australia, community and local services",
        seoDescription:
          "Find practical Australian-life information, ask questions, learn from real experiences, join meetups and browse local services.",
        heroTitle: "Solve Australian life problems quickly, in Korean",
        heroLine1:
          "Understand visas, rent, tax, school, healthcare and work issues, and find the information and services you need right away.",
        heroLine2: "",
        searchPlaceholder:
          "What's the problem? e.g. landlord won't return my bond",
        searchLabel: "Search life in Australia",
        helpPrompt: "Couldn't find what you're looking for? Ask us over Kakao.",
        findTopicsCta: "Browse life problems",
        all: "All",
        commonTopics: "Australian life problems people get stuck on",
        commonTopicsDescription:
          "You do not need to know the official term. Start with the situation closest to yours.",
        viewAllTopics: "View all life problems",
        questions: "Questions & Solved Experiences",
        questionsDescription:
          "Ask a question, share how you solved something, or just talk with neighbours.",
        moreQuestions: "View Neighbourhood Talk",
        emptyQuestions: "There are no questions or stories to show here yet.",
        emptyQuestionsDescription: "Start by asking a practical question.",
        intentQuestion: "Questions",
        intentExperience: "Solved Experiences",
        intentLegacy: "Neighbourhood Talk",
        meetups: "Korean community groups",
        meetupsDescription:
          "Find real participation opportunities with a host, schedule and location.",
        moreMeetups: "More meetups & events",
        emptyMeetups: "There are no meetups to show right now.",
        emptyMeetupsDescription:
          "Try another area or browse all meetups.",
        myArea: "Life in your area",
        myAreaDescription: `Meetups and Local Market posts for ${selectedZone.label} in one place.`,
        write: "Create",
        localNews: "Local updates",
        localServices: "Local services",
        localMarket: "Local Market",
        noNews: "There are no local updates to show right now.",
        noServices: "There are no services to show right now.",
        noMarket: "There are no Market posts to show right now.",
        services: "Nearby Korean-speaking services",
        servicesDescription: "Find services you can talk to in Korean.",
        browseServices: "Browse all services",
        news: "News and updates",
        newsDescription:
          "Check major news and local updates relevant to life in Australia.",
        moreNews: "More news",
        emptyNews: "There are no news items to show right now.",
        emptyNewsDescription: "Browse the news page for the latest posts.",
        market: "Local Market",
        marketDescription:
          "Buy, sell and browse used goods, cars and property through one Local Market.",
        marketEyebrow: "Unified Local Market",
        browseMarket: "Browse all Market posts",
        shortcuts: {
          topics: "News",
          area: "Local Market",
          meetups: "Neighbourhood Talk",
          services: "Find services",
        },
      };

  const { data: localSections = emptyLocalSections, isLoading: isLocalLoading } =
    useQuery<HomeLocalSections>({
      queryKey: ["home-today-zone", selectedZoneId, locale],
      queryFn: async () => {
        const zoneParam = encodeURIComponent(selectedZoneId);
        const response = await fetch(
          `/api/unified-feed?zone=${zoneParam}&limit=30`,
        );
        if (!response.ok) throw new Error("Failed to fetch Home feed");

        const feedCards = getArrayPayload(await response.json());
        const byType = (type: string) =>
          feedCards.filter((item) => item.type === type);

        const mapItems = (
          type: string,
          limit: number,
          fallbackHref: string,
          fallbackMeta: string,
        ) =>
          byType(type)
            .map((item, index) => ({
              title: optionalTextValue(item.title),
              id: textValue(item.id, `${type}-${index}`),
              subtitle: optionalTextValue(item.subtitle) ?? "",
              href: textValue(item.href, fallbackHref),
              meta: textValue(item.meta, fallbackMeta),
            }))
            .filter((item): item is HomeLocalItem => Boolean(item.title))
            .slice(0, limit);

        return {
          market: mapItems(
            "market",
            3,
            "/market",
            selectedZone.label,
          ),
          talk: mapItems(
            "talk",
            3,
            "/community-talk",
            isKorean ? "질문·경험" : "Questions & Stories",
          ),
          meetings: mapItems(
            "meetup",
            3,
            "/meetings",
            selectedZone.label,
          ),
          businesses: mapItems(
            "business",
            3,
            "/business-directory",
            selectedZone.label,
          ),
          news: mapItems(
            "news",
            3,
            "/news",
            isKorean ? "뉴스" : "News",
          ),
        };
      },
    });

  const submitSearch = () => {
    const query = searchQuery.trim();
    if (!query) return;
    const params = new URLSearchParams({ q: query });
    if (selectedCategory) {
      params.set("category", selectedCategory.id);
    }
    window.location.href = `/search?${params.toString()}`;
  };

  const selectedCategoryLabel = selectedCategory
    ? isKorean
      ? selectedCategory.label
      : searchCategoryLabelsEn[selectedCategory.id] || selectedCategory.label
    : copy.all;

  const shortcuts = [
    {
      href: "/news",
      label: copy.shortcuts.topics,
      icon: Newspaper,
      testId: "shortcut-life-topics",
    },
    {
      href: "/market",
      label: copy.shortcuts.area,
      icon: MapPin,
      testId: "shortcut-my-area",
      anchor: false,
    },
    {
      href: "/community-talk",
      label: copy.shortcuts.meetups,
      icon: MessageCircle,
      testId: "shortcut-meetups-events",
    },
    {
      href: "/business-directory",
      label: copy.shortcuts.services,
      icon: Building2,
      testId: "shortcut-find-services",
    },
  ];

  return (
    <>
      <Seo
        title={copy.seoTitle}
        description={copy.seoDescription}
        path="/"
        keywords={[
          "호주 생활",
          "호주 비자",
          "호주 렌트",
          "호주 운전면허",
          "호주 세금",
          "호주 한인 커뮤니티",
          "Australia Korean community",
        ]}
      />

      <main className="min-h-screen bg-background">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 via-background to-background">
          <div className="mx-auto w-full max-w-[1536px] px-4 py-10 sm:py-14 md:px-6 md:py-20 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mb-5 flex justify-center md:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLocale(isKorean ? "en" : "ko")}
                  className="gap-2"
                  aria-label={
                    isKorean ? "Switch to English" : "한국어로 전환"
                  }
                >
                  <Languages className="h-4 w-4" />
                  {isKorean ? "English" : "한국어"}
                </Button>
              </div>

              <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">
                {copy.heroTitle}
              </h1>
              <p className="mx-auto mt-4 max-w-3xl text-sm leading-6 text-muted-foreground sm:mt-6 sm:text-base sm:leading-7 md:text-lg">
                {copy.heroLine1}
                {copy.heroLine2 && (
                  <>
                    <br className="hidden sm:block" /> {copy.heroLine2}
                  </>
                )}
              </p>

              <div className="mx-auto mt-6 flex max-w-4xl flex-col gap-3 sm:mt-8 sm:flex-row">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className="h-14 justify-between gap-3 bg-background sm:min-w-40"
                      data-testid="button-category-selector"
                    >
                      <span>{selectedCategoryLabel}</span>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => setSelectedCategory(null)}>
                      {copy.all}
                    </DropdownMenuItem>
                    {homeSearchCategories.map((category) => (
                      <DropdownMenuItem
                        key={category.id}
                        onClick={() => setSelectedCategory(category)}
                        data-testid={`menu-category-${category.id}`}
                      >
                        {isKorean
                          ? category.label
                          : searchCategoryLabelsEn[category.id] || category.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="flex min-w-0 flex-1 gap-2">
                  <Input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") submitSearch();
                    }}
                    placeholder={copy.searchPlaceholder}
                    aria-label={copy.searchLabel}
                    className="h-14 min-w-0 flex-1 bg-background px-4 text-base"
                    data-testid="input-search"
                  />
                  <Button
                    type="button"
                    size="lg"
                    onClick={submitSearch}
                    className="h-14 px-5 sm:px-7"
                    aria-label={copy.searchLabel}
                    data-testid="button-search"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="mx-auto mt-4 flex max-w-4xl flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <Link href="/topics">
                  <Button size="lg" className="w-full gap-2 sm:w-auto" data-testid="button-browse-topics">
                    {copy.findTopicsCta}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                {isKorean && (
                  <HelpRequestButton sourceType="search" size="lg" className="w-full sm:w-auto" />
                )}
              </div>
              {isKorean && (
                <p className="mt-3 text-sm text-muted-foreground">{copy.helpPrompt}</p>
              )}

              <div className="mx-auto mt-6 grid max-w-3xl grid-cols-2 gap-2 sm:mt-7 sm:grid-cols-4 sm:gap-3">
                {shortcuts.map((shortcut) => {
                  const content = (
                    <div
                      className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card p-3 text-center transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:bg-accent"
                      data-testid={shortcut.testId}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <shortcut.icon className="h-5 w-5" />
                      </span>
                      <span className="text-xs font-semibold text-foreground sm:text-sm">
                        {shortcut.label}
                      </span>
                    </div>
                  );

                  return shortcut.anchor ? (
                    <a key={shortcut.href} href={shortcut.href}>
                      {content}
                    </a>
                  ) : (
                    <Link key={shortcut.href} href={shortcut.href}>
                      {content}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1536px] px-4 py-12 md:px-6 md:py-16 lg:px-8">
          <SectionHeader
            eyebrow="OZK Life Guide"
            title={copy.commonTopics}
            description={copy.commonTopicsDescription}
            href="/topics"
            actionLabel={copy.viewAllTopics}
          />
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {commonTopics.map((topic) => {
              const category = TOPIC_CATEGORIES.find(
                (item) => item.id === topic.category,
              );
              const problem = getTopicProblem(topic.id);
              const problemText = problem?.problem[locale];
              const firstAction = problem?.firstAction[locale];

              return (
                <Link key={topic.id} href={`/topics/${topic.id}`}>
                  <article
                    className="group flex h-full min-h-64 flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm"
                    data-testid={`link-common-topic-${topic.id}`}
                  >
                    {category && (
                      <p className="text-xs font-semibold text-primary">
                        {getTopicCategoryLabel(category, locale)}
                      </p>
                    )}
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <h3 className="text-lg font-bold leading-6 text-foreground group-hover:text-primary">
                        {getTopicLabel(topic, locale)}
                      </h3>
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
                    </div>
                    <p className="mt-3 line-clamp-3 text-base font-semibold leading-6 text-foreground">
                      {problemText ?? getTopicDescription(topic, locale)}
                    </p>
                    {problem && (
                      <span className="mt-3 w-fit rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                        {getTopicProblemUrgencyLabel(problem.urgency, locale)}
                      </span>
                    )}
                    <div className="mt-5 border-t border-border pt-4">
                      <p className="text-xs font-semibold text-muted-foreground">
                        {isKorean ? "지금 먼저 할 일" : "What to do first"}
                      </p>
                      <p className="mt-2 text-sm font-medium leading-5 text-foreground">
                        {firstAction ?? (isKorean ? "내 상황과 필요한 절차를 확인하세요." : "Check the steps that match your situation.")}
                      </p>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="border-y border-border bg-muted/30">
          <div className="mx-auto w-full max-w-[1536px] px-4 py-12 md:px-6 md:py-16 lg:px-8">
            <SectionHeader
              eyebrow={isKorean ? "오늘 확인할 변화" : "What changed today"}
              title={copy.news}
              description={copy.newsDescription}
              href="/news"
              actionLabel={copy.moreNews}
            />
            <div className="mt-7">
              <ItemGrid
                items={localSections.news}
                isLoading={isLocalLoading}
                emptyTitle={copy.emptyNews}
                emptyDescription={copy.emptyNewsDescription}
                emptyHref="/news"
              />
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1536px] px-4 py-12 md:px-6 md:py-16 lg:px-8">
          <SectionHeader
            eyebrow="OZK Services"
            title={copy.services}
            description={copy.servicesDescription}
            href="/business-directory"
            actionLabel={copy.browseServices}
          />
          <div className="mt-7 grid grid-cols-2 gap-3 md:grid-cols-4">
            {serviceCategories.map((category) => (
              <Link
                key={category.ko}
                href={`/business-directory?category=${category.category}`}
              >
                <div className="flex min-h-20 items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:bg-accent">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {isKorean ? category.ko : category.en}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="border-y border-border bg-muted/30">
          <div className="mx-auto w-full max-w-[1536px] px-4 py-12 md:px-6 md:py-16 lg:px-8">
            <SectionHeader
              eyebrow="OZK Life Community"
              title={copy.questions}
              description={copy.questionsDescription}
              href="/community-talk"
              actionLabel={copy.moreQuestions}
            />
            {isKorean && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Link href="/community-talk?intent=question">
                  <Button variant="outline" size="sm">{copy.intentQuestion}</Button>
                </Link>
                <Link href="/community-talk?intent=experience">
                  <Button variant="outline" size="sm">{copy.intentExperience}</Button>
                </Link>
                <Link href="/community-talk?intent=legacy">
                  <Button variant="outline" size="sm">{copy.intentLegacy}</Button>
                </Link>
              </div>
            )}
            <div className="mt-7">
              <ItemGrid
                items={localSections.talk}
                isLoading={isLocalLoading}
                emptyTitle={copy.emptyQuestions}
                emptyDescription={copy.emptyQuestionsDescription}
                emptyHref="/questions/new"
              />
            </div>
          </div>
        </section>

        <section id="my-area" className="scroll-mt-24 mx-auto w-full max-w-[1536px] px-4 py-12 md:px-6 md:py-16 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold text-primary">OZK Local</p>
              <h2 className="mt-1 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                {copy.myArea}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground md:text-base">
                {copy.myAreaDescription}
              </p>
            </div>
            <ZoneSelector compact showSuburbs={false} />
          </div>

          <div className="mt-7 grid gap-4 lg:grid-cols-2">
            <LocalPanel
              icon={MessageCircle}
              title={copy.meetups}
              href="/meetings"
              items={localSections.meetings}
              isLoading={isLocalLoading}
              emptyTitle={copy.emptyMeetups}
            />
            <LocalPanel
              icon={ShoppingBag}
              title={copy.localMarket}
              href="/market"
              items={localSections.market}
              isLoading={isLocalLoading}
              emptyTitle={copy.noMarket}
            />
          </div>
        </section>
      </main>
    </>
  );
}
